import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useEffect, useState } from 'react';

import translations from 'src/App/App.vocab';
import {
  FlowKey,
  UserFormProvider,
} from 'src/App/Components/UserFormProvider/UserFormProvider';
import { PageLayout } from 'src/App/Layouts/PageLayout/PageLayout';
import {
  ADD_USER_EMAIL,
  ADD_USER_DETAILS,
  PAGE_USER_DETAILS_DISPLAYED,
  PAGE_USER_EMAIL_DISPLAYED,
} from 'src/constants/tracking';
import {
  useHirerUserEmailV2LazyQuery,
  type HirerUserEmailFoundPayloadV2,
  type HirerUserEmailV2Query,
} from 'src/graphql/generated.ts';
import { useTracking } from 'src/hooks/use-track-page-load';

import { VerifyEmail } from './VerifyEmail/VerifyEmail';

const isUserNotFound = (user: HirerUserEmailV2Query) =>
  user.hirerUserEmailV2.__typename === 'HirerUserEmailNotFoundPayloadV2';

const useSearchHirerUser = () => {
  const [findUserByEmail, { data: userEmailDetails, error, loading }] =
    useHirerUserEmailV2LazyQuery({
      fetchPolicy: 'network-only',
    });

  return { findUserByEmail, userEmailDetails, error, loading };
};

export const AddUserPage = () => {
  const { t } = useTranslations(translations);
  const { findUserByEmail, userEmailDetails, error } = useSearchHirerUser();
  const [email, setEmail] = useState('');

  useEffect(() => {
    const lookupUserEmail = async (e: string) => {
      await findUserByEmail({ variables: { input: { email: e } } });
    };

    if (email) {
      lookupUserEmail(email);
    }
  }, [email, findUserByEmail]);

  const isEmailInAdCentre = (emailDetails: HirerUserEmailV2Query) => {
    const userNotFound = isUserNotFound(emailDetails);

    if (userNotFound) {
      return {};
    }

    const foundUser =
      emailDetails.hirerUserEmailV2 as HirerUserEmailFoundPayloadV2;
    return { user: foundUser };
  };

  const props = userEmailDetails ? isEmailInAdCentre(userEmailDetails) : {};

  return (
    <PageLayout heading={t('Add team member title')}>
      <AddUserFlow
        data={userEmailDetails}
        error={error}
        props={props}
        email={email}
        setEmail={setEmail}
      />
    </PageLayout>
  );
};

interface AddUserFlowProps {
  data?: HirerUserEmailV2Query;
  error?: ApolloError;
  props?: { user: HirerUserEmailFoundPayloadV2 } | { user: undefined };
  email: string;
  setEmail: (email: string) => void;
}

const AddUserFlow = ({
  data,
  error,
  props,
  email,
  setEmail,
}: AddUserFlowProps) => {
  const { trackView } = useTracking();
  useEffect(() => {
    if (!data) {
      trackView(PAGE_USER_EMAIL_DISPLAYED, ADD_USER_EMAIL);
      return;
    }
    trackView(PAGE_USER_DETAILS_DISPLAYED, ADD_USER_DETAILS);
  }, [data, trackView]);

  if (data) {
    return (
      <UserFormProvider
        flowKey={FlowKey.Add}
        userExistingOnAdd={props?.user}
        providedEmail={email}
        {...props}
      />
    );
  }
  return <VerifyEmail setEmail={setEmail} error={error} />;
};
